import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Avatar,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { resolveProviderLogoUrl } from '../utils/resolveProviderLogoUrl';

interface LogoUploadProps {
  providerId: number;
  currentLogo?: string;
  currentLogoUrl?: string;
  onLogoChange: (logoUrl: string | null) => void;
  disabled?: boolean;
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  providerId,
  currentLogo,
  currentLogoUrl,
  onLogoChange,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resolveHttpErrorMessage = (err: any, fallback: string): string => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const backendMessage =
      data?.error ||
      data?.message ||
      (typeof data === 'string' ? data : null);

    if (backendMessage) {
      return status ? `[${status}] ${backendMessage}` : backendMessage;
    }

    if (status === 413) {
      return 'Le serveur refuse le fichier (413 Payload Too Large). Vérifie la limite upload du serveur web/proxy.';
    }

    if (err?.message) {
      return status ? `[${status}] ${err.message}` : err.message;
    }

    return fallback;
  };

  const ALLOWED_LOGO_EXTENSIONS = ['svg', 'png', 'jpg', 'jpeg'] as const;

  const isAllowedLogoFile = (file: File): boolean => {
    const ext = file.name.toLowerCase().split('.').pop() || '';
    const normalizedExt = ext === 'jpeg' ? 'jpg' : ext;
    if (ALLOWED_LOGO_EXTENSIONS.includes(normalizedExt as (typeof ALLOWED_LOGO_EXTENSIONS)[number])) {
      return true;
    }
    return (
      file.type === 'image/svg+xml' ||
      file.type === 'image/svg' ||
      file.type === 'image/png' ||
      file.type === 'image/jpeg' ||
      file.type === 'image/jpg'
    );
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedLogoFile(file)) {
      setError('Formats autorisés : SVG, PNG ou JPG.');
      return;
    }

    // Vérifier la taille (max 4MB)
    if (file.size > 4 * 1024 * 1024) {
      setError('Le fichier ne doit pas dépasser 4MB.');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await apiService.uploadProviderLogo(providerId, formData);
      onLogoChange(response.data.logo_url);
    } catch (error: any) {
      console.error('Erreur lors de l\'upload:', error);
      setError(resolveHttpErrorMessage(error, 'Erreur lors de l\'upload du logo'));
    } finally {
      setUploading(false);
      // Réinitialiser l'input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!currentLogo) return;

    setUploading(true);
    setError('');

    try {
      await apiService.deleteProviderLogo(providerId);
      onLogoChange(null);
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      setError(resolveHttpErrorMessage(error, 'Erreur lors de la suppression du logo'));
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Logo du partenaire
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper 
        variant="outlined" 
        sx={{ 
          p: 3, 
          textAlign: 'center',
          border: '2px dashed',
          borderColor: 'grey.300',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={disabled ? undefined : handleUploadClick}
      >
        {currentLogoUrl || currentLogo ? (
          <Box>
            <Avatar
              src={resolveProviderLogoUrl(currentLogoUrl, currentLogo) || undefined}
              sx={{ 
                width: 120, 
                height: 120, 
                mx: 'auto', 
                mb: 2,
                border: '2px solid',
                borderColor: 'primary.main',
              }}
              variant="rounded"
            >
              <ImageIcon fontSize="large" />
            </Avatar>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Logo actuel
            </Typography>
            
            <Box display="flex" gap={1} justifyContent="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUploadIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadClick();
                }}
                disabled={uploading || disabled}
              >
                Remplacer
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteLogo();
                }}
                disabled={uploading || disabled}
              >
                Supprimer
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon 
              sx={{ 
                fontSize: 48, 
                color: 'grey.400', 
                mb: 2 
              }} 
            />
            
            <Typography variant="h6" gutterBottom>
              Ajouter un logo
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Cliquez pour sélectionner un logo
            </Typography>
            
            <Typography variant="caption" color="text.secondary">
              SVG, PNG ou JPG • Max 4MB
            </Typography>
          </Box>
        )}

        {uploading && (
          <Box 
            position="absolute" 
            top={0} 
            left={0} 
            right={0} 
            bottom={0} 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            bgcolor="rgba(255, 255, 255, 0.8)"
            sx={{ position: 'absolute' }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>

      <input
        ref={fileInputRef}
        type="file"
        accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />
    </Box>
  );
};

export default LogoUpload;
